import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Slide,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "24px",
    boxShadow:
      "0 32px 64px rgba(5, 50, 81, 0.2), 0 16px 32px rgba(5, 50, 81, 0.1)",
    background: "linear-gradient(145deg, #ffffff 0%, #f8fffe 100%)",
    border: "1px solid rgba(124, 200, 144, 0.1)",
    position: "relative",
    overflow: "visible",
    maxWidth: "520px",
    width: "100%",
    margin: "16px",
  },
  "& .MuiBackdrop-root": {
    backgroundColor: "rgba(5, 50, 81, 0.4)",
    backdropFilter: "blur(8px)",
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background:
    "linear-gradient(135deg, rgba(5, 50, 81, 0.02) 0%, rgba(124, 200, 144, 0.02) 100%)",
  borderBottom: "1px solid rgba(5, 50, 81, 0.08)",
  padding: "32px 70px 24px 32px",
  position: "relative",
  direction: "rtl",
  textAlign: "right",
  "& span": {
    fontSize: "28px",
    fontWeight: "700",
    background: "linear-gradient(135deg, #053251 0%, #2a5a72 100%)",
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    lineHeight: "1.3",
    letterSpacing: "-0.5px",
    display: "block",
    width: "100%",
  },
}));

const StyledCloseButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  left: "16px",
  top: "16px",
  backgroundColor: "rgba(5, 50, 81, 0.05)",
  color: "#053251",
  width: "40px",
  height: "40px",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    backgroundColor: "#053251",
    color: "white",
    transform: "rotate(90deg)",
    boxShadow: "0 8px 16px rgba(5, 50, 81, 0.2)",
  },
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: "32px",
  paddingTop: "24px",
  paddingBottom: "24px",
  position: "relative",
  direction: "rtl",
}));

const StyledDialogContentText = styled(DialogContentText)(({ theme }) => ({
  marginRight: "24px",
  textAlign: "right",
  direction: "rtl",
  "& p": {
    fontSize: "16px",
    lineHeight: "1.75",
    color: "#053251",
    marginBottom: "16px",
    fontWeight: "400",
    letterSpacing: "0.1px",
    position: "relative",
    textAlign: "right",
    "&:last-child": {
      marginBottom: 0,
    },
    "&.bold": {
      fontWeight: "700",
    },
    "&.danger": {
      color: "#e53935",
      fontWeight: "600",
    },
  },
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: "24px 32px 32px",
  background:
    "linear-gradient(135deg, rgba(255, 107, 107, 0.03) 0%, rgba(5, 50, 81, 0.01) 100%)",
  borderTop: "1px solid rgba(5, 50, 81, 0.06)",
  gap: "16px",
  justifyContent: "flex-start",
  direction: "rtl",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "32px",
    right: "32px",
    height: "1px",
    background:
      "linear-gradient(90deg, transparent 0%, rgba(124, 200, 144, 0.3) 50%, transparent 100%)",
  },
}));

const StyledButton = styled(Button)(
  ({ theme, variant: buttonVariant, color }) => ({
    borderRadius: "14px",
    padding: "14px 32px",
    fontSize: "15px",
    fontWeight: "600",
    textTransform: "none",
    letterSpacing: "0.5px",
    minWidth: "120px",
    position: "relative",
    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
    boxShadow: "none",
    ...(color === "error" && {
      background: "linear-gradient(135deg, #ff6b6b 0%, #e55a5a 100%)",
      color: "white",
      border: "1px solid rgba(255, 107, 107, 0.3)",
      "&:hover": {
        background: "linear-gradient(135deg, #e55a5a 0%, #d14949 100%)",
        boxShadow: "0 12px 24px rgba(255, 107, 107, 0.3)",
        transform: "translateY(-2px)",
      },
      "&:active": { transform: "translateY(0)" },
    }),
    ...(color === "success" && {
      background: "linear-gradient(135deg, #7cc890 0%, #6bb57f 100%)",
      color: "white",
      border: "1px solid rgba(124, 200, 144, 0.3)",
      "&:hover": {
        background: "linear-gradient(135deg, #6bb57f 0%, #5aa46e 100%)",
        boxShadow: "0 12px 24px rgba(124, 200, 144, 0.3)",
        transform: "translateY(-2px)",
      },
      "&:active": { transform: "translateY(0)" },
    }),
    ...(color === "primary" && {
      background: "linear-gradient(135deg, #053251 0%, #2a5a72 100%)",
      color: "white",
      border: "1px solid rgba(5, 50, 81, 0.3)",
      "&:hover": {
        background: "linear-gradient(135deg, #2a5a72 0%, #1e4159 100%)",
        boxShadow: "0 12px 24px rgba(5, 50, 81, 0.3)",
        transform: "translateY(-2px)",
      },
      "&:active": { transform: "translateY(0)" },
    }),
    "&::before": {
      content: '""',
      position: "absolute",
      inset: 0,
      borderRadius: "14px",
      padding: "1px",
      background:
        color === "success"
          ? "linear-gradient(135deg, rgba(124, 200, 144, 0.4), transparent)"
          : "linear-gradient(135deg, rgba(5, 50, 81, 0.4), transparent)",
      mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
      maskComposite: "xor",
      WebkitMask:
        "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
      WebkitMaskComposite: "xor",
    },
  }),
);

const styleClassMap = {
  normal: "",
  bold: "bold",
  danger: "danger",
};

const ConfirmModal = ({
  handleCloseDialog,
  handleConfirm,
  openDialog,
  text,
  cancleText,
  confirmText,
  title,
  children,
}) => {
  return (
    <StyledDialog
      open={openDialog}
      onClose={handleCloseDialog}
      TransitionComponent={Transition}
      keepMounted
      maxWidth={false}
      disableEnforceFocus
      sx={{ zIndex: 2000 }}
    >
      <StyledDialogTitle>
        <span className="mb-3">{title}</span>
        <StyledCloseButton onClick={handleCloseDialog}>
          <CloseIcon />
        </StyledCloseButton>
      </StyledDialogTitle>

      <StyledDialogContent>
        {Array.isArray(text) && text.length > 0 && (
          <StyledDialogContentText className="mt-3">
            {text.map((line, index) => {
              if (React.isValidElement(line)) {
                return (
                  <p className="iran-sans" key={index}>
                    {line}
                  </p>
                );
              }
              if (typeof line === "object" && line !== null) {
                return (
                  <p
                    className={`iran-sans ${styleClassMap[line.style] || ""}`}
                    key={index}
                  >
                    {line.text}
                  </p>
                );
              }
              return (
                <p className="iran-sans" key={index}>
                  {line}
                </p>
              );
            })}
          </StyledDialogContentText>
        )}
        {children}
      </StyledDialogContent>

      <StyledDialogActions>
        <StyledButton
          variant="contained"
          onClick={handleCloseDialog}
          color="primary"
          className="ms-2"
          component={StyledButton}
        >
          <span>{cancleText}</span>
        </StyledButton>
        <StyledButton
          onClick={handleConfirm}
          color="success"
          variant="contained"
          component={StyledButton}
        >
          <span>{confirmText}</span>
        </StyledButton>
      </StyledDialogActions>
    </StyledDialog>
  );
};

export default ConfirmModal;
